import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSave } from "lib/@hooks/use-save"
import { ToBeSaved } from "lib/@components/save-indicator/save-indicator"
import { DraftSyncSuccess, DraftSyncConflict } from "event-definitions"
import {
    saveLocalDraft,
    getLocalDraft,
    clearLocalDraft,
    clearLocalDraftIfNotNewer,
    saveEmergencyDraft,
    migrateEmergencyDrafts,
    updateLocalDraftMetadata,
} from "lib/@offline/local-draft-store"
import { syncDraftNow } from "lib/@offline/draft-sync-manager"
import { withTimeout } from "lib/@offline/with-timeout"
import { isSame } from "library/normalized-stringify/index.mjs"
import { getCurrentWriteOrderSnapshot } from "lib/graphql/write-order-link"

const SAVE_TIMEOUT_MS = 15000

/**
 * Create a blocking promise that can be settled later for cleanup.
 * Used to prevent useSave's .then() from firing ToBeSaved(false)
 * after we've already set a "queued" or "conflict" state.
 * The abort callback resolves the promise so GC can reclaim closures.
 * Resolving (rather than rejecting) avoids noisy console.error logs
 * from useSave's catch handler — the ToBeSaved event guard already
 * re-raises the correct status when useSave's .then() fires.
 */
function createBlockingPromise() {
    let abort
    const promise = new Promise((resolve) => {
        abort = () => resolve()
    })
    return { promise, abort }
}

/**
 * Wraps useSave with an IndexedDB-backed local draft layer for offline resilience.
 * Any save failure (network, timeout, server error, high latency) falls back to
 * a locally-queued draft that syncs automatically when saves succeed again.
 *
 * @param {object} options
 * @param {function} options.save - Server save function
 * @param {object} options.target - Data being edited
 * @param {string} options.draftKey - localDraftStore key
 * @param {string} options.editorType - "custom-schedule" or "regime-tailoring"
 * @param {*} options.serverVersion - Version when editing began
 * @param {string} options.id - For ToBeSaved events
 * @param {number} [options.wait=1000] - Debounce wait
 * @param {function} [options.onConflict] - Called when a conflict is detected
 * @param {function} [options.onRestore] - Called with restored draft data and metadata when a local draft is found on mount
 * @param {function} [options.preparePayload] - Enriches the payload before it is stored in IndexedDB and sent to the server save function. Use this to inject fields that live in refs (e.g. draftReason) so the queued draft is self-contained for offline replay.
 * @returns {{ onChange: function, status: string }}
 */
export function useOfflineSave({
    save: serverSave,
    target,
    draftKey,
    editorType,
    serverVersion,
    id = "tailor",
    wait = 1000,
    onConflict,
    onRestore,
    saveContext,
    preparePayload,
}) {
    const [status, _setStatus] = useState("idle")
    const statusRef = useRef(status)
    // Update both the state and ref together so the ref is never stale
    // when event handlers read it before React re-renders.
    function updateStatus(s) {
        statusRef.current = s
        _setStatus(s)
    }
    const draftKeyRef = useRef(draftKey)
    draftKeyRef.current = draftKey
    const serverVersionRef = useRef(serverVersion)
    serverVersionRef.current = serverVersion
    const editorTypeRef = useRef(editorType)
    editorTypeRef.current = editorType
    const targetRef = useRef(target)
    targetRef.current = target
    const saveContextRef = useRef(saveContext)
    saveContextRef.current = saveContext
    const preparePayloadRef = useRef(preparePayload)
    preparePayloadRef.current = preparePayload
    const dirtyRef = useRef(false)
    const blockingPromiseRef = useRef(null)

    // Wrap the server save to write to IndexedDB first, then attempt server save
    const wrappedSave = useCallback(
        async function wrappedSave(payload) {
            dirtyRef.current = true
            const key = draftKeyRef.current

            // Enrich the payload before storing or sending so the queued
            // draft contains all audit-critical fields (e.g. draftReason).
            const prepared = preparePayloadRef.current ? preparePayloadRef.current(payload) : payload

            if (!key) return serverSave(prepared)

            // Abort any previous blocking promise so its closures can be GC'd
            if (blockingPromiseRef.current) {
                blockingPromiseRef.current.abort()
                blockingPromiseRef.current = null
            }

            // Try to write to IndexedDB first - this is the safety net.
            // If local storage fails (quota, disabled), we can't claim "queued" on server failure.
            let localDraftSaved = false
            const savedAt = Date.now()
            try {
                await saveLocalDraft(
                    key,
                    prepared,
                    serverVersionRef.current,
                    editorTypeRef.current,
                    saveContextRef.current
                )
                localDraftSaved = true
            } catch (e) {
                console.warn("Local draft write failed, falling back to server-only save:", e?.message || e)
            }

            // Attempt the server save. Use a nested try so network throws and null-result
            // (Apollo swallowed network error) are handled independently without one path
            // accidentally being re-caught by the other's error handler.
            let result
            try {
                result = await withTimeout(serverSave(prepared), SAVE_TIMEOUT_MS)
            } catch (e) {
                if (localDraftSaved) {
                    await rememberQueuedWriteOrderSeq(key)
                    // Draft is safe in IndexedDB, queue for later sync.
                    console.warn("Save failed, draft queued locally:", e?.message || e)
                    updateStatus("queued")
                    if (id) ToBeSaved(id).raise("queued")
                    const bp = createBlockingPromise()
                    blockingPromiseRef.current = bp
                    return bp.promise
                }

                // Both local storage and server save failed - data is genuinely at risk.
                // Surface the real error so the UI shows "Error", not "Saved locally".
                console.error("Save failed and local draft storage unavailable:", e?.message || e)
                updateStatus("error")
                if (id) ToBeSaved(id).raise(new Error("Save failed"))
                throw e
            }

            // Apollo's error link swallows network errors ("Failed to fetch") and
            // returns { data: null } instead of throwing. Detect this and treat as failure.
            // This is intentionally outside the try-catch above so it goes directly to
            // useSave's handleError without being re-caught and double-handled.
            if (result == null) {
                if (localDraftSaved) {
                    await rememberQueuedWriteOrderSeq(key)
                    console.warn("Save returned empty result (likely network error), draft queued locally")
                    updateStatus("queued")
                    if (id) ToBeSaved(id).raise("queued")
                    const bp = createBlockingPromise()
                    blockingPromiseRef.current = bp
                    return bp.promise
                }
                // Both failed — data is genuinely at risk.
                console.error("Save returned empty result and local draft storage unavailable - data may be lost")
                updateStatus("error")
                if (id) ToBeSaved(id).raise(new Error("Save failed"))
                throw new Error("Save failed: network error and local storage unavailable")
            }

            // Check for conflict response from server
            if (result && typeof result === "object" && result.conflict) {
                if (!localDraftSaved) {
                    updateStatus("error")
                    if (id) ToBeSaved(id).raise(new Error("Conflict without local recovery"))
                    throw new Error("Conflict detected but local draft storage unavailable")
                }
                await rememberQueuedWriteOrderSeq(key)
                updateStatus("conflict")
                if (id) ToBeSaved(id).raise(new Error("Conflict"))
                DraftSyncConflict.raise({ key, result })
                const bp = createBlockingPromise()
                blockingPromiseRef.current = bp
                return bp.promise
            }

            // Check for stale write-order rejection — the server dropped this write
            if (result && typeof result === "object" && result.stale) {
                if (localDraftSaved) {
                    await rememberQueuedWriteOrderSeq(key)
                    console.warn("Save rejected as stale (out-of-order write), draft queued locally")
                    updateStatus("queued")
                    if (id) ToBeSaved(id).raise("queued")
                    const bp = createBlockingPromise()
                    blockingPromiseRef.current = bp
                    return bp.promise
                }
                updateStatus("error")
                if (id) ToBeSaved(id).raise(new Error("Save failed"))
                throw new Error("Save rejected as stale and local draft storage unavailable")
            }

            // Check for skipped save (e.g., lock lost during editing) — nothing was
            // persisted. Surface "queued" so the UI shows the draft is pending rather
            // than leaving the status stuck at whatever it was before, then block
            // useSave from signalling success so it doesn't clear the dirty state.
            if (result && typeof result === "object" && result.skipped) {
                if (!localDraftSaved) {
                    updateStatus("error")
                    if (id) ToBeSaved(id).raise(new Error("Save failed"))
                    throw new Error("Save skipped and local draft storage unavailable")
                }
                await rememberQueuedWriteOrderSeq(key)
                updateStatus("queued")
                if (id) ToBeSaved(id).raise("queued")
                const bp = createBlockingPromise()
                blockingPromiseRef.current = bp
                return bp.promise
            }

            // Check for explicit server failure (e.g., draft doesn't exist, missing
            // content _id, or id mismatch). The server returns { success: false }
            // without a conflict flag — treat the same as a skipped save so the
            // local draft is preserved and queued for retry rather than silently
            // cleared.
            if (result && typeof result === "object" && result.success === false) {
                if (localDraftSaved) {
                    await rememberQueuedWriteOrderSeq(key)
                    console.warn("Server save returned success:false, draft queued locally")
                    updateStatus("queued")
                    if (id) ToBeSaved(id).raise("queued")
                    const bp = createBlockingPromise()
                    blockingPromiseRef.current = bp
                    return bp.promise
                }
                updateStatus("error")
                if (id) ToBeSaved(id).raise(new Error("Save failed"))
                throw new Error("Server save failed and local draft storage unavailable")
            }

            // Success - clear the local draft only if no newer version was written while in-flight
            if (localDraftSaved) await clearLocalDraftIfNotNewer(key, savedAt)
            updateStatus("saved")
            dirtyRef.current = false
            return result
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [serverSave, id]
    )

    const rawOnChange = useSave({ save: wrappedSave, target, id, wait }, [draftKey])

    // Set dirtyRef immediately when the user triggers a change, before the debounced
    // wrappedSave fires. This ensures the beforeunload emergency save fires even if
    // the user navigates away within the debounce window.
    const onChange = useMemo(() => {
        const handler = (...args) => {
            dirtyRef.current = true
            rawOnChange(...args)
        }
        handler.flush = rawOnChange.flush
        return handler
    }, [rawOnChange])

    // Guard against useSave's no-change path raising ToBeSaved(false) when drafts
    // are still queued locally. useSave assumes "content unchanged = saved" but when
    // offline, unchanged content just means the queued draft hasn't been edited further.
    // The suppressRef prevents repeated re-raises: once we've corrected false → queued,
    // we skip further corrections until the status genuinely changes.
    const suppressRef = useRef(null)
    ToBeSaved(id).useEvent(
        (value) => {
            if (value !== false) {
                suppressRef.current = null
                // User is editing again while a blocking promise holds useSave's
                // inFlight lock — abort the promise so useSave can dispatch a new save.
                if (value === true && blockingPromiseRef.current) {
                    blockingPromiseRef.current.abort()
                    blockingPromiseRef.current = null
                }
                return
            }
            const s = statusRef.current
            if ((s === "queued" || s === "conflict") && suppressRef.current !== s) {
                suppressRef.current = s
                setTimeout(() => ToBeSaved(id).raise(s === "conflict" ? new Error("Conflict") : "queued"), 0)
            }
        },
        [id]
    )

    // Listen for sync manager events
    DraftSyncSuccess.useEvent(
        (event) => {
            if (event?.key === draftKeyRef.current) {
                if (blockingPromiseRef.current) {
                    blockingPromiseRef.current.abort()
                    blockingPromiseRef.current = null
                }
                updateStatus("saved")
                dirtyRef.current = false
                if (id) ToBeSaved(id).raise(false)
            }
        },
        [id]
    )

    DraftSyncConflict.useEvent(
        (event) => {
            if (event?.key === draftKeyRef.current) {
                if (blockingPromiseRef.current) {
                    blockingPromiseRef.current.abort()
                    blockingPromiseRef.current = null
                }
                updateStatus("conflict")
                if (onConflict) onConflict(event)
            }
        },
        [id, onConflict]
    )

    // On mount: migrate emergency drafts, then check for existing local draft
    useEffect(() => {
        if (!draftKeyRef.current) return () => {}
        let cancelled = false
        restoreLocalDraft()
        return () => {
            cancelled = true
        }

        async function restoreLocalDraft() {
            await migrateEmergencyDrafts()
            if (cancelled) return
            const draft = await getLocalDraft(draftKeyRef.current)
            if (cancelled) return
            if (draft) {
                if (dirtyRef.current) return
                if (onRestore) onRestore(draft.data, draft)
                updateStatus("queued")
                dirtyRef.current = true
                if (id) ToBeSaved(id).raise("queued")
                if (navigator.onLine) {
                    setTimeout(async () => {
                        const syncResult = await syncDraftNow(draftKeyRef.current)
                        if (cancelled) return
                        const remaining = await getLocalDraft(draftKeyRef.current)
                        if (cancelled) return
                        if (syncResult?.status === "conflict" || remaining?.conflictDetected) return
                        if (syncResult?.status !== "synced" && syncResult?.status !== "noop" && remaining) return
                        if (remaining && !isSame(remaining.data, draft.data)) return
                        if (remaining) await clearLocalDraft(draftKeyRef.current)
                        if (blockingPromiseRef.current) {
                            blockingPromiseRef.current.abort()
                            blockingPromiseRef.current = null
                        }
                        updateStatus("saved")
                        dirtyRef.current = false
                        if (id) ToBeSaved(id).raise(false)
                    }, 0)
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // beforeunload emergency save to localStorage (synchronous fallback)
    useEffect(() => {
        window.addEventListener("beforeunload", emergencySave)
        return () => window.removeEventListener("beforeunload", emergencySave)

        function emergencySave() {
            if (!dirtyRef.current) return
            const key = draftKeyRef.current
            const currentStatus = statusRef.current
            if (key && targetRef.current && currentStatus !== "saved") {
                saveEmergencyDraft(
                    key,
                    targetRef.current,
                    serverVersionRef.current,
                    editorTypeRef.current,
                    saveContextRef.current
                )
            }
        }
    }, [])

    // Abort blocking promises on unmount to release closures
    useEffect(
        () => () => {
            if (blockingPromiseRef.current) {
                blockingPromiseRef.current.abort()
                blockingPromiseRef.current = null
            }
        },
        []
    )

    return { onChange, status }

    async function rememberQueuedWriteOrderSeq(key) {
        const orderKey = saveContextRef.current?.writeOrderKey
        if (!key || !orderKey) return
        const writeOrder = getCurrentWriteOrderSnapshot(orderKey)
        if (!writeOrder?.seq) return
        await updateLocalDraftMetadata(key, {
            saveContext: {
                ...(saveContextRef.current ?? {}),
                writeOrderKey: orderKey,
                writeOrderSeq: writeOrder.seq,
                writeOrderInstance: writeOrder.instance,
                writeOrderSentAt: new Date().toISOString(),
            },
        })
    }
}
